/**
 * 翻译状态管理
 *
 * 提供翻译状态管理和相关操作的核心钩子
 *
 * 功能:
 * 1. 管理翻译状态(源文本、翻译结果、选中文本等)
 * 2. 提供翻译操作(设置选中文本、翻译选中文本、重置状态等)
 * 3. 集成异步操作处理
 * 4. 处理错误状态
 */
import { useState, useCallback, useRef } from 'react';
import { TranslationManager } from '../services/TranslationManager.js';
import { useModelState } from '../hooks/useModelState.js';
import { useLanguageState } from '../../language/hooks/useLanguageState.js';
import { prepareTranslationArgs } from '../utils/index.js';
/**
 * 初始翻译状态
 */
const initialState = {
    originalText: '',
    translatedText: '',
    selection: null,
    analysis: undefined,
    isLoading: false,
    streamingText: '',
};
/**
 * 使用翻译状态钩子
 * 管理翻译状态并提供相关操作方法
 *
 * @returns 翻译状态和操作方法
 */
export function useTranslationState() {
    // 创建翻译管理器实例
    const translationManager = useRef(TranslationManager.getInstance()).current;
    // 使用全局模型状态
    const { model } = useModelState();
    // 使用全局语言状态
    const { sourceLanguage: globalSourceLanguage, targetLanguage: globalTargetLanguage } = useLanguageState();
    // 管理翻译状态
    const [state, setState] = useState(initialState);
    // 重置翻译状态
    const resetTranslationState = useCallback(() => {
        setState(initialState);
    }, []);
    // 设置选中文本
    const setSelectedText = useCallback((data) => {
        setState(prev => ({
            ...prev,
            selection: data,
            // Do not clear previous translation results when a new selection is made
        }));
    }, []);
    // 设置原始文本
    const startStreamingTranslation = useCallback(async (options, onComplete) => {
        const { selection } = state;
        if (!selection) {
            return;
        }
        const preparedArgs = prepareTranslationArgs({
            selection: selection,
            allDocumentParagraphs: options?.allDocumentParagraphs,
        });
        if (!preparedArgs) {
            console.error('[TranslationState] Failed to prepare translation arguments from selection.', selection);
            return;
        }
        const { textToTranslate, contextualPrompt, originalSelection } = preparedArgs;
        setState(prev => ({
            ...prev,
            isLoading: true,
            streamingText: '',
            translatedText: '',
            originalText: '',
            analysis: undefined,
            error: undefined,
        }));
        let finalResponseForCallback = null;
        const managerOptions = {
            ...options,
            selection: originalSelection,
        };
        // [REVISED] Using a flatter structure to avoid linter issues with try/catch/finally.
        let caughtError = null;
        try {
            const stream = await translationManager.translateWithContextStream(textToTranslate, contextualPrompt.prefix, contextualPrompt.suffix, globalSourceLanguage, globalTargetLanguage, model, managerOptions);
            for await (const chunk of stream) {
                if (typeof chunk === 'string') {
                    // This is the only setState call inside the loop, for live UI updates.
                    setState(prev => ({ ...prev, streamingText: prev.streamingText + chunk }));
                }
                else if (chunk && typeof chunk === 'object' && 'translatedText' in chunk) {
                    finalResponseForCallback = chunk;
                }
            }
        }
        catch (error) {
            caughtError = error instanceof Error ? error : new Error(String(error));
            console.error('Error caught during stream processing:', caughtError);
        }
        // FINALIZATION: All state updates and callbacks are handled here, outside the loop.
        if (caughtError) {
            setState(prev => ({ ...prev, isLoading: false, error: caughtError }));
            throw caughtError;
        }
        else {
            setState(prev => ({
                ...prev,
                isLoading: false,
                streamingText: '',
                originalText: finalResponseForCallback?.originalText ?? textToTranslate,
                translatedText: finalResponseForCallback?.translatedText ?? '',
                analysis: finalResponseForCallback?.analysis,
                error: undefined,
            }));
            if (onComplete) {
                onComplete(finalResponseForCallback);
            }
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        state.selection,
        setState,
        translationManager,
        globalSourceLanguage,
        globalTargetLanguage,
        model,
    ]);
    // 设置显示的翻译结果（通常用于从缓存加载）
    const setDisplayedTranslation = useCallback((data) => {
        setState(prev => ({
            ...prev,
            originalText: data.originalText,
            translatedText: data.translatedText,
            analysis: data.analysis,
            streamingText: '',
            isLoading: false,
            error: undefined,
        }));
    }, [setState]);
    // 返回状态和方法
    return {
        translationState: state,
        isLoading: state.isLoading,
        error: state.error,
        resetTranslationState,
        setSelectedText,
        startStreamingTranslation,
        setDisplayedTranslation,
    };
}
//# sourceMappingURL=TranslationState.js.map